import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Client, type Room } from 'colyseus.js';
import { SQUAD_CALLS, type SquadCall } from '@bb/shared';
import { bombSquad, social, type ProgressNews, type SquadResult } from '../net/api';

/**
 * Bomb Squad, shown as Bubble Squad to players aged 8–14. Same game and rules;
 * only the words and the little icons change. The server keeps the layout and
 * answers each reveal with just the squares that opened.
 */
export type Brand = 'bubble' | 'bomb';
const WORDS: Record<Brand, { game: string; one: string; many: string; pop: string; host: string }> = {
  bubble: { game: 'Bubble Squad', one: 'bubble', many: 'bubbles', pop: 'Pop!', host: 'Fizz the pufferfish hid bubbles under the sand' },
  bomb: { game: 'Bomb Squad', one: 'bomb', many: 'bombs', pop: 'Boom!', host: 'Fizz the pufferfish hid fizz bombs under the sand' },
};
export const squadGameName = (b: Brand) => WORDS[b].game;

const HIDDEN = -2;
const POPPED = 9;
const BOMB_SHOWN = 10;
const DIFFICULTIES = [
  { key: 'rookie', name: 'Rookie', size: '8 × 8', count: 10 },
  { key: 'agent', name: 'Agent', size: '12 × 12', count: 24 },
  { key: 'chief', name: 'Chief', size: '16 × 16', count: 40 },
] as const;

function callText(call: SquadCall, b: Brand) {
  const w = WORDS[b];
  return { danger: `${w.one[0]!.toUpperCase()}${w.one.slice(1)} here!`, safe: 'Safe here', flag: 'Flag this one', nice: 'Nice!', oops: 'Oops!', help: 'Help me out?' }[call];
}

function Icon({ brand, popped }: { brand: Brand; popped?: boolean }) {
  return brand === 'bubble' ? (
    <svg viewBox="0 0 20 20" className="sq-icon" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" fill={popped ? '#f08a74' : '#9fdde6'} stroke="#16808f" strokeWidth="1.5" />
      <circle cx="7.5" cy="7.5" r="2" fill="#ffffff" opacity=".85" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" className="sq-icon" aria-hidden="true">
      <circle cx="9" cy="11.5" r="6.5" fill={popped ? '#d4553e' : '#1d2b36'} />
      <path d="M12.5 6.5 L15 3.5" stroke="#9a6a43" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="15.6" cy="2.9" r="1.6" fill="#e9a915" />
      <circle cx="7" cy="9.5" r="1.6" fill="#ffffff" opacity=".6" />
    </svg>
  );
}
const FlagIcon = () => (
  <svg viewBox="0 0 20 20" className="sq-icon" aria-hidden="true">
    <path d="M6 3 V17" stroke="#1d2b36" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M6.5 3.5 L15 6.5 L6.5 9.5 Z" fill="#e9a915" />
  </svg>
);

/** The grid, shared by solo and Squad play. `cells`: -2 hidden, 0–8 number, 9 popped, 10 shown at the end. */
function Board(p: {
  w: number;
  cells: number[];
  flags: boolean[];
  brand: Brand;
  scanned?: Map<number, boolean>;
  cursors?: { i: number; color: string; name: string }[];
  disabled?: boolean;
  onOpen: (i: number) => void;
  onFlag: (i: number) => void;
  onHover?: (i: number) => void;
}) {
  const press = useRef<number | undefined>(undefined);
  const longPress = (i: number) => ({
    // Touch: hold a square for half a second to flag it.
    onTouchStart: () => {
      press.current = window.setTimeout(() => {
        press.current = undefined;
        p.onFlag(i);
      }, 450);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      if (press.current === undefined) e.preventDefault();
      else window.clearTimeout(press.current);
    },
  });
  return (
    <div className="sq-board" style={{ gridTemplateColumns: `repeat(${p.w}, minmax(0, 1fr))` }} role="grid" aria-label="Game board">
      {p.cells.map((v, i) => {
        const hidden = v === HIDDEN;
        const scan = p.scanned?.get(i);
        const cursor = p.cursors?.find((c) => c.i === i);
        const label = hidden ? (p.flags[i] ? 'Flagged' : 'Hidden square') : v === POPPED || v === BOMB_SHOWN ? WORDS[p.brand].one : v === 0 ? 'Empty' : String(v);
        return (
          <button
            key={i}
            className={`sq-cell ${hidden ? 'hidden' : 'open'} ${v === POPPED ? 'popped' : ''} ${scan === true ? 'scan-bomb' : scan === false ? 'scan-safe' : ''}`}
            data-n={v > 0 && v < 9 ? v : undefined}
            style={cursor ? { boxShadow: `inset 0 0 0 3px ${cursor.color}` } : undefined}
            title={cursor ? cursor.name : undefined}
            disabled={p.disabled || (!hidden && v !== POPPED)}
            aria-label={label}
            onClick={() => hidden && !p.flags[i] && p.onOpen(i)}
            onContextMenu={(e: MouseEvent) => {
              e.preventDefault();
              if (hidden) p.onFlag(i);
            }}
            onMouseEnter={() => p.onHover?.(i)}
            {...longPress(i)}
          >
            {hidden ? (p.flags[i] ? <FlagIcon /> : scan === true ? <span className="sq-sense"><Icon brand={p.brand} /></span> : null) : v === POPPED || v === BOMB_SHOWN ? <Icon brand={p.brand} popped={v === POPPED} /> : v > 0 ? v : ''}
          </button>
        );
      })}
    </div>
  );
}

function ResultCard({ r, brand, onAgain }: { r: SquadResult; brand: Brand; onAgain: () => void }) {
  return (
    <div className="result">
      <strong className="big-score">{r.won ? 'Board cleared!' : `${WORDS[brand].pop} Better luck next time.`}</strong>
      {r.flagged ? (
        <p className="form-error">That game was too fast to be real, so it didn't count.</p>
      ) : (
        <p>
          {r.won && <>{r.seconds} seconds · {r.score} points · </>}+{r.coins} coins · +{r.xp} XP
          {r.leveledUp && <strong> · Level up! You're now level {r.level}.</strong>}
        </p>
      )}
      {r.dailyCapReached && <p className="muted small-print">You've reached today's coin limit for this game. XP still counts.</p>}
      <button className="btn" onClick={onAgain}>Play again</button>
    </div>
  );
}

export function BombSquad({ brand, joinRoomId, onDone }: { brand: Brand; joinRoomId?: string; onDone: (news?: ProgressNews) => void }) {
  const [mode, setMode] = useState<{ kind: 'menu' } | { kind: 'solo'; difficulty: (typeof DIFFICULTIES)[number]['key'] } | { kind: 'squad'; roomId: string }>(
    joinRoomId ? { kind: 'squad', roomId: joinRoomId } : { kind: 'menu' },
  );
  const [round, setRound] = useState(0);
  const back = () => {
    setMode({ kind: 'menu' });
    setRound((n) => n + 1);
  };
  if (mode.kind === 'solo') return <Solo key={round} brand={brand} difficulty={mode.difficulty} onDone={onDone} onMenu={back} />;
  if (mode.kind === 'squad') return <Squad key={mode.roomId} brand={brand} roomId={mode.roomId} onDone={onDone} onMenu={back} />;
  return <Menu brand={brand} onSolo={(d) => setMode({ kind: 'solo', difficulty: d })} onSquad={(roomId) => setMode({ kind: 'squad', roomId })} />;
}

function Menu({ brand, onSolo, onSquad }: { brand: Brand; onSolo: (d: 'rookie' | 'agent' | 'chief') => void; onSquad: (roomId: string) => void }) {
  const w = WORDS[brand];
  const [friends, setFriends] = useState<{ username: string; displayName: string | null; online: boolean }[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    social.overview().then((r) => setFriends(r.friends), () => setFriends([]));
  }, []);
  async function invite(name: string) {
    try {
      const r = await bombSquad.invite(name);
      onSquad(r.roomId);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const online = friends?.filter((f) => f.online) ?? [];
  return (
    <div className="sq-menu">
      <p>{w.host}. The numbers tell you how many {w.many} touch each square. Open every safe square to win; flag the {w.many} with a right-click (or hold on a tablet). You'll never need to guess.</p>
      <div className="sq-levels">
        {DIFFICULTIES.map((d) => (
          <button key={d.key} className="sq-level" onClick={() => onSolo(d.key)}>
            <strong>{d.name}</strong>
            <span className="muted small-print">{d.size} · {d.count} {w.many}</span>
          </button>
        ))}
      </div>
      <h3 className="mini-head">Squad mode: play with a friend</h3>
      <p className="muted small-print">Clear one big board together with 3 shared lives. Only friends who are online can join.</p>
      {friends === undefined ? (
        <p className="muted">Checking who's online…</p>
      ) : online.length === 0 ? (
        <p className="muted small-print">None of your friends are online right now.</p>
      ) : (
        <div className="row-wrap">
          {online.map((f) => (
            <button key={f.username} className="btn ghost small" onClick={() => void invite(f.username)}>
              Invite {f.username}
            </button>
          ))}
        </div>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
    </div>
  );
}

function useTimer(running: boolean) {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!running) return;
    const start = Date.now() - s * 1000;
    const id = window.setInterval(() => setS(Math.floor((Date.now() - start) / 1000)), 250);
    return () => window.clearInterval(id);
  }, [running]);
  return s;
}

function Solo({ brand, difficulty, onDone, onMenu }: { brand: Brand; difficulty: 'rookie' | 'agent' | 'chief'; onDone: (n?: ProgressNews) => void; onMenu: () => void }) {
  const w = WORDS[brand];
  const [game, setGame] = useState<{ sessionId: string; w: number; h: number; bombs: number }>();
  const [cells, setCells] = useState<number[]>([]);
  const [flags, setFlags] = useState<boolean[]>([]);
  const [scanned, setScanned] = useState<Map<number, boolean>>(new Map());
  const [sonarArmed, setSonarArmed] = useState(false);
  const [sonarUsed, setSonarUsed] = useState(false);
  const [flagMode, setFlagMode] = useState(false);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<SquadResult>();
  const [error, setError] = useState<string>();
  const busy = useRef(false);
  const seconds = useTimer(started && !result);

  useEffect(() => {
    bombSquad.start(difficulty).then(
      (g) => {
        setGame(g);
        setCells(Array(g.w * g.h).fill(HIDDEN));
        setFlags(Array(g.w * g.h).fill(false));
      },
      (e: Error) => setError(e.message),
    );
  }, [difficulty]);

  const flag = (i: number) => setFlags((f) => f.map((x, j) => (j === i ? !x : x)));
  async function open(i: number) {
    if (!game || result || busy.current) return;
    if (flagMode) return flag(i);
    if (sonarArmed) {
      setSonarArmed(false);
      try {
        const r = await bombSquad.sonar(game.sessionId, i);
        setSonarUsed(true);
        setScanned(new Map(r.scanned.map((x) => [x.i, x.bomb])));
      } catch (e) {
        setError((e as Error).message);
      }
      return;
    }
    busy.current = true;
    try {
      const r = await bombSquad.reveal(game.sessionId, i);
      setStarted(true);
      setCells((c) => {
        const next = [...c];
        for (const o of r.opened) next[o.i] = o.n;
        if (r.boom) {
          for (const b of r.bombs ?? []) if (next[b] === HIDDEN) next[b] = BOMB_SHOWN;
          next[i] = POPPED;
        } else if (r.won) for (const b of r.bombs ?? []) next[b] = BOMB_SHOWN;
        return next;
      });
      if (r.result) {
        setResult(r.result);
        onDone(r.result.progress);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      busy.current = false;
    }
  }

  if (error && !game) return <p className="form-error">{error}</p>;
  if (!game) return <p className="muted">Hiding the {w.many}…</p>;
  const flagged = flags.filter(Boolean).length;
  return (
    <div className="sq-game">
      <div className="sq-hud">
        <span><strong>{Math.max(0, game.bombs - flagged)}</strong> {w.many} left</span>
        <span className="tnum">{seconds}s</span>
        <button className={flagMode ? 'btn small' : 'btn small ghost'} onClick={() => setFlagMode((x) => !x)} aria-pressed={flagMode}>Flag mode</button>
        <button className={sonarArmed ? 'btn small' : 'btn small ghost'} disabled={sonarUsed || !started || !!result} onClick={() => setSonarArmed((x) => !x)} aria-pressed={sonarArmed}>
          {sonarUsed ? 'Sonar used' : sonarArmed ? 'Pick a square to scan' : 'Sonar (once)'}
        </button>
        <span className="spacer" />
        <button className="link-btn" onClick={onMenu}>← Levels</button>
      </div>
      {sonarArmed && <p className="muted small-print">Sonar shows which hidden squares in a 3 × 3 area hold a {w.one}. Using it skips the no-Sonar bonus.</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
      <Board w={game.w} cells={cells} flags={flags} brand={brand} scanned={scanned} disabled={!!result} onOpen={(i) => void open(i)} onFlag={flag} />
      {result && <ResultCard r={result} brand={brand} onAgain={onMenu} />}
    </div>
  );
}

interface SquadSnap {
  phase: string;
  w: number;
  h: number;
  bombs: number;
  lives: number;
  cells: number[];
  flags: number[];
  players: Record<string, { name: string; cursor: number; color: string }>;
  seconds: number;
}

function Squad({ brand, roomId, onDone, onMenu }: { brand: Brand; roomId: string; onDone: (n?: ProgressNews) => void; onMenu: () => void }) {
  const w = WORDS[brand];
  const [room, setRoom] = useState<Room>();
  const [snap, setSnap] = useState<SquadSnap>();
  const [result, setResult] = useState<SquadResult>();
  const [calls, setCalls] = useState<{ from: string; text: string; at: number }[]>([]);
  const [flagMode, setFlagMode] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let left = false;
    let r: Room | undefined;
    new Client(`${window.location.origin}/realtime`).joinById(roomId).then(
      (joined) => {
        if (left) return void joined.leave(true);
        r = joined;
        setRoom(joined);
        joined.onStateChange((s) => setSnap((s as unknown as { toJSON: () => SquadSnap }).toJSON()));
        joined.onMessage('result', (res: SquadResult) => {
          setResult(res);
          onDone(res.progress);
        });
        joined.onMessage('call', (m: { from: string; call: SquadCall }) => setCalls((c) => [...c.slice(-4), { from: m.from, text: callText(m.call, brand), at: Date.now() }]));
        joined.onMessage('popped', () => {});
        joined.onLeave((code) => {
          if (code !== 1000) setError('The Squad game ended.');
        });
      },
      (e: Error) => setError(e.message.includes('not found') ? 'That Squad game has already closed.' : e.message),
    );
    return () => {
      left = true;
      void r?.leave(true);
    };
  }, [roomId]);

  if (error && !snap) return (
    <div className="stack">
      <p className="form-error">{error}</p>
      <button className="btn ghost" onClick={onMenu}>Back</button>
    </div>
  );
  if (!snap || !room) return <p className="muted">Joining your squad…</p>;
  const players = Object.entries(snap.players);
  const others = players.filter(([id]) => id !== room.sessionId).map(([, p]) => ({ i: p.cursor, color: p.color, name: p.name }));
  const flags = snap.flags.map(Boolean);
  const over = snap.phase === 'won' || snap.phase === 'lost';
  return (
    <div className="sq-game">
      <div className="sq-hud">
        <span>{players.map(([, p]) => p.name).join(' & ')}</span>
        <span className="sq-lives" aria-label={`${snap.lives} lives left`}>{Array.from({ length: 3 }, (_, k) => <span key={k} className={k < snap.lives ? 'life' : 'life lost'} />)}</span>
        <span><strong>{Math.max(0, snap.bombs - flags.filter(Boolean).length - snap.cells.filter((c) => c === POPPED).length)}</strong> {w.many} left</span>
        <span className="tnum">{snap.seconds}s</span>
        <button className={flagMode ? 'btn small' : 'btn small ghost'} onClick={() => setFlagMode((x) => !x)} aria-pressed={flagMode}>Flag mode</button>
        <span className="spacer" />
        <button className="link-btn" onClick={onMenu}>Leave</button>
      </div>
      {snap.phase === 'waiting' && <p className="note-ok">Waiting for your friend to join…</p>}
      <Board
        w={snap.w}
        cells={snap.cells}
        flags={flags}
        brand={brand}
        cursors={others}
        disabled={snap.phase !== 'playing'}
        onOpen={(i) => room.send(flagMode ? 'flag' : 'reveal', { i })}
        onFlag={(i) => room.send('flag', { i })}
        onHover={(i) => room.send('cursor', { i })}
      />
      <div className="row-wrap sq-calls" aria-label="Squad calls">
        {SQUAD_CALLS.map((c) => (
          <button key={c} className="btn ghost small" disabled={snap.phase !== 'playing'} onClick={() => room.send('call', { call: c })}>{callText(c, brand)}</button>
        ))}
      </div>
      <ul className="sq-log" aria-live="polite">
        {calls.map((c) => <li key={c.at}><strong>{c.from}:</strong> {c.text}</li>)}
      </ul>
      {over && result && <ResultCard r={result} brand={brand} onAgain={onMenu} />}
      {over && !result && <p className="muted">{snap.phase === 'won' ? 'Board cleared!' : w.pop}</p>}
    </div>
  );
}
